<template>
  <div class="table table-scroll div-block-3 table-import">
    <Spinner v-if="loading || fieldsLoading" center :size="50" class="page-loader" />

    <div v-else-if="list && !list.id">{{ $t('No imported lists found') }}</div>

    <div v-else :style="tableWidth" class="grid-table grid-table-scrollable grid-list">
      <ImportListChoiceRow
        :order="fieldsOrder"
        :options="[...ACTION_FIELDS, ...ALL_FIELDS]"
        :fakes="fakes"
        @recognize="recognize"
      />

      <ImportListHeader :order="fieldsOrder" />

      <ImportListRow
        v-for="(row, idx) in list.rows"
        :key="row.id"
        v-bind="row"
        :index="idx"
        :number="row.rowIndex"
        :order="fieldsOrder"
        :row-index="row.sheet_row_index"
        :errors="getRowError(row.sheet_row_index)"
        @remove="$emit('removeRow', row.id)"
        @edit-cell-value="(params) => $emit('edit-cell-value', params)"
      />
    </div>
  </div>
</template>

<script>
  import Spinner from 'vue-simple-spinner';
  import ImportListChoiceRow from '@/components/import/ListChoiceRow';
  import ImportListHeader from '@/components/import/ListHeader';
  import { compileFields } from '@/utils/fields/fields-import';
  import ImportListRow from '@/components/import/ListRow';
  import parseError from '@/utils/formatting/parse-error';
  import factory from '@/utils/api/factory';
  import { mapActions } from 'vuex';
  import { COLUMN_WIDTHS } from '@/utils/fields/order-rows';

  export default {
    name: 'ImportList',
    components: {
      ImportListRow,
      ImportListHeader,
      ImportListChoiceRow,
      Spinner,
    },
    props: {
      list: {
        type: Object,
        default: () => ({
          id: null,
          order: null,
          title: '',
          created_at: null,
          fields_order: [],
          recognized_fields: {},
          rows: [],
        }),
      },
      loading: {
        type: Boolean,
        default: false,
      },

      errors: {
        type: Array,
        default: () => [],
      },
    },
    data() {
      return {
        fakes: [],
        fieldsLoading: false,
        PREDEFINED_FIELD_TYPES: [],
        ACTION_FIELDS: [],
        ALL_FIELDS: [],
      };
    },

    async created() {
      this.fieldsLoading = true;
      try {
        let { data } = await factory('import').indexInPath('properties');
        this.PREDEFINED_FIELD_TYPES = data.predefined_fields;
        this.ACTION_FIELDS = compileFields(data.action_fields);
        this.ALL_FIELDS = compileFields(data.recognizable_fields, data.required_fields);
        this.fieldsLoading = false;
      } catch (e) {
        console.error(e);
      }
    },
    computed: {
      /**
       * Function computes the order, names and widths of the list table from the given (from the
       * back-end) recognized fields {[key: string of field slug]: string of field type} and
       * fields_order [{[key: string of field slug]: string of field name}]
       *
       * @returns field = {
       *   custom: boolean,
       *   slug: string,
       *   text: string,
       *   recognized: string,
       *   width: 'wide'|'narrow'
       * }
       */
      fieldsOrder() {
        const keys = Object.keys(this.list.recognized_fields);
        if (!keys.length) return [];

        const fields = [];
        this.list.fields_order.forEach((orderObj) => {
          const key = Object.keys(orderObj)?.[0];
          if (!key) return;

          const fieldType = this.list.recognized_fields[key];
          this.PREDEFINED_FIELD_TYPES.forEach((field) => {
            if (fieldType === field) {
              const predefined = this.ALL_FIELDS.find((some) => some.slug === fieldType) || null;
              const action = this.ACTION_FIELDS.find((some) => some.slug === fieldType) || null;

              fields.push({
                ...predefined,
                ...action,
                slug: key,
                text: orderObj[key],
                systemText: key === predefined?.slug ? predefined?.text : orderObj[key],
                recognized: predefined?.slug || action?.slug,
                custom: !predefined,
              });
            }
          });
        });

        return fields;
      },
      tableWidth() {
        const widths = this.fieldsOrder.map((field) => {
          if (field.custom) {
            if (this.list.rows.some((row) => row.attrs?.custom_fields?.[field.slug]?.length > 16))
              return COLUMN_WIDTHS.wide;
            else return COLUMN_WIDTHS.narrow;
          } else if (field.width === 'wide') return COLUMN_WIDTHS.wide;
          else if (field.width === 'narrow') return COLUMN_WIDTHS.narrow;
          else return COLUMN_WIDTHS.narrow;
        });

        const pixelWidth = 100 + widths.reduce((accum, val) => accum + val.pixels, 0);
        const style = widths
          .map((space) => `minmax(${space.pixels}px, ${space.fractions})`)
          .join(' ');

        return {
          '-ms-grid-columns': `50px ${style}`,
          'grid-template-columns': `50px ${style}`,
          width: pixelWidth + 'px',
        };
      },
    },

    methods: {
      ...mapActions({
        createError: 'error/create',
      }),

      async recognize({ field, val }) {
        try {
          if (field.recognized !== 'ignore' || !val.fake) {
            await factory(`import/list/${this.list.id}/recognize-field`).customPost({
              field: field.slug,
              recognized_as: val.fake ? 'ignore' : val.slug,
            });
          }

          this.fakes = this.fakes.filter((fake) => fake.current !== field.slug);
          if (val.fake) this.fakes = [...this.fakes, { ...val, current: field.slug }];

          this.$emit('update');
        } catch (e) {
          this.createError(parseError(e) || e);
          console.error(parseError(e) || e); // eslint-disable-line
        }
      },

      getRowError(id) {
        return this.errors?.find((err) => err.id === id);
      },
    },
  };
</script>
